import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@garnish/overlays';

import { type useBundleContent } from '@order/components-menu';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Tells the guest none of the products could be added. The bundle stays open
 * with every product still selected. Copy from Contentful wins over the
 * bundled copy.
 */
export const useBundleFailedToAddToBagNotice = (params: Params) => {
  const { failureBanner } = params;

  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();

  return useCallback(() => {
    addNoticeBanner({
      palette: 'caution',
      text: failureBanner ?? formatMessage(messages.failedToAddToBag),
      autoHideTimeout: NOTICE_AUTO_HIDE_TIMEOUT,
    });
  }, [addNoticeBanner, failureBanner, formatMessage]);
};

// ─── Constants ───────────────────────────────────────────────────────────────

const NOTICE_AUTO_HIDE_TIMEOUT = 4000;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  failedToAddToBag: {
    defaultMessage: 'We couldn’t add selected products. Please try again later.',
    description: 'Bundle | Added to bag notice | No product could be added',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = Pick<BundleContent, 'failureBanner'>;

type BundleContent = ReturnType<typeof useBundleContent>['content'];
